/**
 * plp-searchanise-controller.js
 *
 * Drives the /search PLP by reading URL state, fetching Searchanise REST,
 * and rendering rail + grid + sort + chips + pagination into the empty
 * shells in sections/product-list-page.liquid (search branch).
 *
 * Runs on /search and /collections/*. Takes over a collection page only after a
 * successful first fetch; on failure it stands down and url-filter-controller
 * keeps the native SSR grid. See window.searchanisePlpOwns.
 *
 * URL convention (we own it, not Shopify):
 *   ?q=boat                                     search query
 *   ?type=product                               always present (legacy fallback)
 *   ?sort_by=title-asc                          sort (see SORT_OPTIONS)
 *   ?page=2                                     pagination (1-indexed)
 *   ?filter.<facet-title>=<value>               facet filter; repeat for multi-value
 *
 * Facet-title-keyed filters: the facet "title" from Searchanise response
 * (e.g. "custom.activity", "Vendor", "Availability"). At fetch time we
 * translate title -> attribute via a map built from the response itself
 * (titleToAttribute), since restrictBy[<attr>] is what the REST endpoint
 * actually wants.
 *
 * IIFE pattern matches theme.js + component-common-search.js.
 */
(function () {
  'use strict';

  /* Runs wherever the PLP root shell exists: /search (inline shell) and, from
     Phase 2, /collections/* (SSR grid tagged with the same data-searchanise-* hooks). */
  if (!document.querySelector('[data-searchanise-plp]')) return;

  /* Ownership is earned by a successful first fetch, not assumed at load.
     url-filter-controller reads this at click time AND at popstate time, since
     it binds both handlers before the first fetch resolves. */
  window.searchanisePlpOwns = false;

  const IS_SEARCH = document.body.classList.contains('template-search');

  /* ===========================================================
     Selectors (data-attribute hooks from product-list-page.liquid)
     =========================================================== */
  const SEL = {
    root: '[data-searchanise-plp]',
    rail: '[data-searchanise-rail]',
    grid: '[data-searchanise-grid]',
    gridList: '[data-searchanise-grid-list]',
    gridEmpty: '[data-searchanise-grid-empty]',
    sortMenu: '[data-searchanise-sort-menu]',
    sortLabel: '[data-searchanise-sort-label]',
    chips: '[data-searchanise-chips]',
    clearAll: '[data-searchanise-clear-all]',
    pagination: '[data-searchanise-pagination]',
    pageTitle: '[data-searchanise-page-title]',
    resultsCount: '[data-searchanise-results-count]',
    activeFilterCount: '[data-searchanise-active-filter-count]',
    railCount: '[data-searchanise-rail-count]'
  };

  /* Fallback rank source. Searchanise can't sort or return the custom
     popularity_rank field on this plan, so ordering is client-side against a
     generated productId -> rank snapshot. Loaded once on first popularity render.
     Regenerate via scripts/pull-popularity-rank-prod.mjs when Plytix reranks. */
  let RANK_MAP = null;
  /* Returns null (not {}) when unusable, so callers can degrade instead of
     silently sorting everything into a tie. Null also lets a later click retry. */
  async function loadRankMap() {
    if (RANK_MAP) return RANK_MAP;
    try {
      const res = await fetch(window.SEARCHANISE_RANK_MAP_URL);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const parsed = await res.json();
      RANK_MAP = (parsed && Object.keys(parsed).length) ? parsed : null;
      if (!RANK_MAP) throw new Error('rank map empty');
    } catch (e) {
      console.error('[plp-searchanise-controller] rank map load failed:', e);
      RANK_MAP = null;
    }
    return RANK_MAP;
  }

  const PAGE_SIZE = 24;
  /* Searchanise returns at most this many items per request regardless of the
     limit asked for. Popularity (client sort) pages through in these chunks,
     accumulates the full result set, orders it in the browser, then paginates
     the sorted array by PAGE_SIZE. */
  const SEARCHANISE_MAX_PER_REQUEST = 250;

  /* Sort options. value = our internal id used in the URL ?sort_by=value.
     sortBy + sortOrder map to Searchanise REST params. */
  const SORT_OPTIONS = [
    /* "Most Popular" is client-sorted (clientSort): Searchanise can't sort the
       custom popularity_rank field on this plan, so the controller fetches the
       full set and orders it in the browser via window.PopularitySort. */
    { value: 'popularity',   label: 'Most Popular',        sortBy: undefined,  sortOrder: undefined, clientSort: true },
    { value: 'relevance',    label: 'Relevance',           sortBy: undefined,  sortOrder: undefined },
    { value: 'title-asc',    label: 'Name: A-Z',           sortBy: 'title',    sortOrder: 'asc'    },
    { value: 'title-desc',   label: 'Name: Z-A',           sortBy: 'title',    sortOrder: 'desc'   },
    { value: 'created-desc', label: 'Newest',              sortBy: 'created',  sortOrder: 'desc'   }
  ];

  /* Relevance needs a query to score against. On a collection browse Searchanise
     has nothing to rank and returns product-id order under a misleading label. */
  const VISIBLE_SORT_OPTIONS = IS_SEARCH
    ? SORT_OPTIONS
    : SORT_OPTIONS.filter(function (o) { return o.value !== 'relevance'; });

  /* Where "Most Popular" lands when the rank map is unavailable. */
  const RANK_FALLBACK_SORT = 'title-asc';

  /* Facets the rail does NOT render. Match by title (display name from
     response). Curated for Star brite's PLP design:
       - custom.maincategory: surfaced as the tile section above the grid
       - Tags: SKU-only on this catalog, not user-facing
       - Vendor, Product type, Collections, Discount: not curated for the
         PLP rail (vendor is always Star brite; product type is internal
         operational categorization; collections overlap with custom.market;
         catalog has no discounts) */
  const RAIL_HIDDEN_FACETS = new Set([
    'custom.maincategory',
    /* Internal Plytix ranking that drives the Most Popular sort. Never a
       customer-facing filter; here so a blank rail setting cannot leak it. */
    'custom.popularity_rank',
    'Tags',
    'Vendor',
    'Product type',
    'Collections',
    'Discount'
  ]);

  /* ===========================================================
     State
     =========================================================== */
  const state = {
    query: '',
    collection: '',
    scopeTitle: '',
    scopeValue: '',
    sortValue: 'relevance',
    page: 1,
    /* { facetTitle: [value1, value2, ...] } — multi-value OR within facet */
    filtersByTitle: {},
    /* { facetTitle: searchaniseAttribute } — built from response, used at fetch time */
    titleToAttribute: {},
    response: null,
    clientSort: false,
    abortController: null
  };

  /* ===========================================================
     Init
     =========================================================== */
  document.addEventListener('DOMContentLoaded', init);

  async function init() {
    if (!window.SearchaniseAdapter) {
      console.error('[plp-searchanise-controller] SearchaniseAdapter missing; leaving the page to the native engine');
      revealSsrGrid();
      return;
    }
    parseUrlToState();

    /* Any surface can be entered on a URL that already carries filters, and
       restrictBy needs the attribute hash: a title is silently ignored and
       returns the whole catalogue. */
    loadCachedAttrMap();

    /* /search has no second engine, so there is no ownership to earn and no
       reason to leave clicks unhandled while the first fetch runs. */
    if (IS_SEARCH) {
      if (hasUnmappedFilters()) await bootstrapAttrMap();
      claimPage();
      await fetchAndRender();
      return;
    }

    /* Feature flag plp_searchanise_collections, emitted by product-list-page.liquid.
       Off means Searchanise never touches a collection page: collection membership
       is not indexed on any store, so restrictBy[collections] returns 0 every time
       and the request is a guaranteed-empty round trip. Stand down immediately and
       let the native engine keep its SSR grid. */
    const root = document.querySelector(SEL.root);
    if (!root || root.dataset.searchaniseCollectionsEnabled !== 'true') {
      revealSsrGrid();
      return;
    }

    /* A collection with no entry in the scope map cannot be scoped, and an
       unscoped fetch would show the whole catalogue on it. Hand it to the native
       engine, which filters it correctly. 'none' is /collections/all, where
       unscoped IS the right answer. */
    const scopeMode = root.dataset.searchaniseScopeMode || 'unmapped';
    if (scopeMode === 'unmapped') {
      revealSsrGrid();
      return;
    }

    /* Scoping needs the attribute hash before the first fetch, or that fetch
       returns the whole catalogue. */
    if (scopeMode === 'facet') {
      if (!scopeAttr()) await bootstrapAttrMap();
      if (!scopeAttr()) {
        console.error('[plp-searchanise-controller] no Searchanise attribute for facet "' + state.scopeTitle + '"; handing the page to the native engine');
        revealSsrGrid();
        return;
      }
    }

    /* Scope and URL filters are separate needs. 'none' (/collections/all) is
       correctly unscoped but still receives the category tiles'
       filter.p.m.custom.maincategory, and a facet-scoped page can be deep-linked
       with rail filters applied. */
    if (hasUnmappedFilters()) await bootstrapAttrMap();

    /* Snapshot the server-rendered markup before the first fetch, because
       fetchAndRender() replaces it before we get to judge the result. */
    const ssr = snapshotSsr();

    const urlAtFetch = window.location.search;
    const ok = await fetchAndRender();
    if (!ok) {
      revealSsrGrid();
      return;
    }

    /* A 200 carrying zero items is not a transport failure, so `ok` is true, but
       on an UNFILTERED collection page that Shopify server-rendered with products
       it cannot be a legitimate answer: it means the Searchanise index is missing
       this collection rather than the collection being empty. Prod hit exactly
       this in 2026-07 when the Collections facet was absent from the account, and
       every collection page went blank while the SSR grid was fine. Hand the page
       back rather than showing a false empty state. */
    if (declinedEmptyIndex(ssr)) {
      restoreSsr(ssr);
      setRootState('ready');
      revealSsrGrid();
      return;
    }

    claimPage();

    /* The native engine was still live during the first fetch, so a click can
       have moved the URL under us. Refetch once now that we own the page. */
    if (window.location.search !== urlAtFetch) {
      parseUrlToState();
      fetchAndRender();
    }
  }

  /* ===========================================================
     Collection scope
     Searchanise cannot scope by collection: membership is not indexed on any
     store, so restrictBy[collections] returns 0 every time. We scope instead by
     the metafield facet the collection's own smart-collection rule uses, mapped
     in the section's "Collection scope" setting.

     restrictBy needs the filter_metafield_<hash> ATTRIBUTE, not the facet title
     (a title is silently ignored and returns the whole catalogue), and those
     hashes are only learned from a response. So the map is bootstrapped once per
     session with a tiny facets-only request and cached.
     =========================================================== */

  const SCOPE_CACHE_KEY = 'searchaniseFacetAttrMap';

  function scopeAttr() {
    if (!state.scopeTitle) return '';
    return state.titleToAttribute[state.scopeTitle] || '';
  }

  /* Titles the URL asked to filter by that we cannot translate to an attribute
     yet. Price.* is synthetic (never a response facet) and is stripped from the
     rail in code, so it never counts. */
  function unmappedFilterTitles() {
    return Object.keys(state.filtersByTitle).filter(function (title) {
      if (title.indexOf('Price.') === 0) return false;
      return !state.titleToAttribute[title];
    });
  }

  function hasUnmappedFilters() {
    return unmappedFilterTitles().length > 0;
  }

  function loadCachedAttrMap() {
    try {
      const raw = window.sessionStorage.getItem(SCOPE_CACHE_KEY);
      if (raw) Object.assign(state.titleToAttribute, JSON.parse(raw));
    } catch { /* private mode; bootstrap will just run again */ }
  }

  function cacheAttrMap() {
    try {
      window.sessionStorage.setItem(SCOPE_CACHE_KEY, JSON.stringify(state.titleToAttribute));
    } catch { /* nothing to do; the map still works for this page */ }
  }

  /* One cheap unscoped request purely to learn title -> attribute. */
  async function bootstrapAttrMap() {
    try {
      const res = await window.SearchaniseAdapter.searchProducts({ query: '', filters: {}, startIndex: 0, limit: 1 });
      (res.facets || []).forEach(function (f) {
        if (f.title && f.attribute) state.titleToAttribute[f.title] = f.attribute;
      });
      cacheAttrMap();
    } catch (err) {
      console.error('[plp-searchanise-controller] facet-map bootstrap failed:', err.code || err.message);
    }
  }

  /* ===========================================================
     SSR handback
     Used only on the first fetch of a collection page. See init().
     =========================================================== */

  function snapshotSsr() {
    const list = document.querySelector(SEL.gridList);
    const rail = document.querySelector(SEL.rail);
    return {
      listHtml: list ? list.innerHTML : null,
      railHtml: rail ? rail.innerHTML : null,
      cardCount: list ? list.querySelectorAll('.c-product-card').length : 0,
      /* Zero results IS valid once the shopper has filtered or searched, so the
         handback only applies to the untouched first view. */
      unfiltered: Object.keys(state.filtersByTitle).length === 0 && !state.query
    };
  }

  function declinedEmptyIndex(ssr) {
    if (!ssr.unfiltered) return false;
    if (ssr.cardCount === 0) return false;
    return !state.response || state.response.total === 0;
  }

  function restoreSsr(ssr) {
    const list = document.querySelector(SEL.gridList);
    const rail = document.querySelector(SEL.rail);
    if (list && ssr.listHtml !== null) list.innerHTML = ssr.listHtml;
    if (rail && ssr.railHtml !== null) rail.innerHTML = ssr.railHtml;
    console.warn('[plp-searchanise-controller] Searchanise returned 0 items for an unfiltered collection that the server rendered with ' + ssr.cardCount + ' products; handing the page back to the native engine. Check the Collections facet in the Searchanise admin.');
  }

  /* Searchanise declined the page, so the Liquid-rendered cards are what the
     shopper actually gets. They mount at opacity 0 under html[data-js]
     (component-plp-grid.css), so hand them to the reveal module or the fallback
     grid stays invisible. */
  function revealSsrGrid() {
    if (IS_SEARCH) return;
    const list = document.querySelector(SEL.gridList);
    if (!list) return;
    if (window.PlpCardReveal) {
      window.PlpCardReveal.observe(list);
    } else {
      list.querySelectorAll('.c-product-card').forEach(function (card) {
        card.setAttribute('data-revealed', 'true');
      });
    }
  }

  function claimPage() {
    window.searchanisePlpOwns = true;
    bindEvents();
    /* plp-side-drawer.js reads this at call time (in applyStagedState and
       applySortValue) to pick the engine that owns the page. It exists only once
       Searchanise has proven it can fetch, so a failed first fetch leaves the
       native engine in charge. */
    window.searchanisePlpRefresh = function () {
      parseUrlToState();
      state.page = 1;
      fetchAndRender();
    };
  }

  /* ===========================================================
     URL state: parse + serialize + push

     URL convention bridges two formats:
       - Shopify metafield: filter.p.m.<title>=<value>     (e.g. filter.p.m.custom.activity)
       - Custom:            filter.<title>=<value>          (everything else)

     State always keys by facet `title` (the display name from Searchanise
     response: "custom.activity", "Vendor", "Availability", "Price.min", etc.).
     Parse normalizes both formats to title. Serialize picks the right
     prefix based on title shape (titles with a dot like "custom.foo" =
     metafield = Shopify prefix; everything else = custom prefix).
     =========================================================== */
  /* Browse (no query) defaults to popularity; an actual search query defaults to
     relevance. serializeStateToUrl uses the same helper so a clean browse URL
     omits sort_by and an explicit popularity-on-a-query is written. */
  function defaultSortFor(query) {
    return query ? 'relevance' : 'popularity';
  }

  function parseUrlToState() {
    const params = new URL(window.location.href).searchParams;
    state.query = params.get('q') || '';
    const rootEl = document.querySelector(SEL.root);
    state.collection = (rootEl && rootEl.dataset.searchaniseCollection) || '';
    state.scopeTitle = (rootEl && rootEl.dataset.searchaniseScopeTitle) || '';
    state.scopeValue = (rootEl && rootEl.dataset.searchaniseScopeValue) || '';
    state.sortValue = params.get('sort_by') || defaultSortFor(state.query);
    /* Unknown or surface-inappropriate sort_by (stale ?sort_by=relevance link on
       a collection, typo'd value) falls back rather than rendering unsorted. */
    if (!VISIBLE_SORT_OPTIONS.some(function (o) { return o.value === state.sortValue; })) {
      state.sortValue = defaultSortFor(state.query);
    }
    state.page = Math.max(1, parseInt(params.get('page') || '1', 10));
    state.filtersByTitle = {};
    params.forEach(function (value, key) {
      if (key.indexOf('filter.') !== 0) return;
      let title;
      if (key.indexOf('filter.p.m.') === 0) {
        title = key.slice('filter.p.m.'.length);
      } else {
        title = key.slice('filter.'.length);
      }
      if (!state.filtersByTitle[title]) state.filtersByTitle[title] = [];
      state.filtersByTitle[title].push(value);
    });
  }

  function serializeStateToUrl() {
    const url = new URL(window.location.href);
    const params = new URLSearchParams();
    if (state.query) params.set('q', state.query);
    params.set('type', 'product');
    if (state.sortValue && state.sortValue !== defaultSortFor(state.query)) {
      params.set('sort_by', state.sortValue);
    }
    if (state.page > 1) params.set('page', String(state.page));
    Object.keys(state.filtersByTitle).forEach(function (title) {
      const prefix = isMetafieldTitle(title) ? 'filter.p.m.' : 'filter.';
      state.filtersByTitle[title].forEach(function (v) {
        params.append(prefix + title, v);
      });
    });
    url.search = params.toString();
    return url;
  }

  function isMetafieldTitle(title) {
    /* Metafield facets are titled with their namespace.key path (e.g.
       "custom.activity", "custom.surface_type"). A dot in the title is
       the cheapest reliable signal. */
    return title.indexOf('.') !== -1;
  }

  function pushUrlState() {
    window.history.pushState({}, '', serializeStateToUrl().toString());
  }

  /* ===========================================================
     Fetch
     =========================================================== */
  /* Rank for a normalized product (variant B: the generated snapshot, since
     Searchanise can't sort or return the field). PopularitySort.parseRank
     normalizes a missing lookup to Infinity, so unranked sorts last. */
  function getRank(product) {
    return RANK_MAP ? RANK_MAP[product.id] : undefined;
  }

  /* Popularity sort orders the whole result set in the browser, but Searchanise
     returns at most SEARCHANISE_MAX_PER_REQUEST items per call, so page through
     with startIndex and accumulate. total + facets come from the first page (they
     describe the full result, not just the returned page). */
  async function fetchAllForClientSort(filters, signal) {
    const base = await window.SearchaniseAdapter.searchProducts({
      query: state.query, scopeAttribute: scopeAttr(), scopeValue: state.scopeValue, filters: filters,
      startIndex: 0, limit: SEARCHANISE_MAX_PER_REQUEST, signal: signal
    });
    const total = base.total || base.products.length;
    let guard = 0;
    while (base.products.length < total && guard < 40) {
      guard += 1;
      const next = await window.SearchaniseAdapter.searchProducts({
        query: state.query, scopeAttribute: scopeAttr(), scopeValue: state.scopeValue, filters: filters,
        startIndex: base.products.length, limit: SEARCHANISE_MAX_PER_REQUEST, signal: signal
      });
      if (!next.products.length) break;
      base.products = base.products.concat(next.products);
    }
    return base;
  }

  async function fetchAndRender() {
    /* A filter we cannot translate must not be sent: Searchanise ignores a raw
       title and answers 200 with the whole catalogue, which renders as a
       successful unfiltered page over a correct SSR result. */
    const unmapped = unmappedFilterTitles();
    if (unmapped.length) {
      console.error('[plp-searchanise-controller] no Searchanise attribute for filter(s): ' + unmapped.join(', '));
      /* Before ownership, declining hands the collection to the native engine,
         which filters it correctly server-side. /search has no such fallback. */
      if (!IS_SEARCH && !window.searchanisePlpOwns) return false;
    }

    if (state.abortController) state.abortController.abort();
    state.abortController = new AbortController();
    setRootState('loading');

    /* Translate filtersByTitle to filtersByAttribute. init() warms the map from
       cache or a bootstrap fetch before the first request, so a miss here means
       the facet is genuinely absent from the index; omit it rather than send a
       title Searchanise will ignore. Price.* is synthetic and passes through. */
    const filtersByAttr = {};
    Object.keys(state.filtersByTitle).forEach(function (title) {
      const attr = state.titleToAttribute[title] ||
        (title.indexOf('Price.') === 0 ? title : '');
      if (!attr) return;
      filtersByAttr[attr] = state.filtersByTitle[title];
    });

    let sortOption = SORT_OPTIONS.find(function (o) { return o.value === state.sortValue; });
    let clientSort = !!(sortOption && sortOption.clientSort);

    /* Client sort needs the rank map. Without it every product ties at Infinity
       and the grid renders Searchanise's default order under a "Most Popular"
       label, so degrade to a real order the shopper can see is different. */
    if (clientSort && !(await loadRankMap())) {
      state.sortValue = RANK_FALLBACK_SORT;
      sortOption = SORT_OPTIONS.find(function (o) { return o.value === state.sortValue; });
      clientSort = false;
    }
    setRankState(RANK_MAP ? 'ok' : 'unavailable');
    state.clientSort = clientSort;

    try {
      let response;
      if (clientSort) {
        response = await fetchAllForClientSort(filtersByAttr, state.abortController.signal);
        window.PopularitySort.sortByRank(response.products, getRank);
      } else {
        response = await window.SearchaniseAdapter.searchProducts({
          query: state.query,
          scopeAttribute: scopeAttr(),
          scopeValue: state.scopeValue,
          filters: filtersByAttr,
          sortBy: sortOption ? sortOption.sortBy : undefined,
          sortOrder: sortOption ? sortOption.sortOrder : undefined,
          startIndex: (state.page - 1) * PAGE_SIZE,
          limit: PAGE_SIZE,
          signal: state.abortController.signal
        });
      }

      state.response = response;

      /* Rebuild titleToAttribute from response facets so subsequent filter
         clicks translate correctly to restrictBy[<attribute>]. */
      response.facets.forEach(function (f) {
        if (f.title && f.attribute) state.titleToAttribute[f.title] = f.attribute;
      });
      cacheAttrMap();

      renderAll();
      setRootState(response.total > 0 ? 'ready' : 'empty');
      return true;
    } catch (err) {
      if (err.name === 'AbortError') return false;
      console.error('[plp-searchanise-controller] fetch failed:', err.code || err.message);
      setRootState('error');
      renderError();
      return false;
    }
  }

  /* Observability hook for the rank-map degrade; no CSS depends on it. */
  function setRankState(s) {
    const root = document.querySelector(SEL.root);
    if (root) root.dataset.searchaniseRank = s;
  }

  function setRootState(s) {
    const root = document.querySelector(SEL.root);
    if (root) root.dataset.searchaniseState = s;
    /* Trigger the existing data-loading-dim CSS by setting data-loading
       on the Shopify section wrapper. Matches what url-filter-controller
       does on /collections so /search gets the same opacity-dim feedback
       during fetch. */
    if (!root) return;
    const sectionEl = root.closest('[id^="shopify-section-"]');
    if (sectionEl) {
      sectionEl.dataset.loading = (s === 'loading') ? 'true' : 'false';
    }
  }

  /* ===========================================================
     Render: orchestration
     =========================================================== */
  function renderAll() {
    renderTitle();
    renderResultsCount();
    renderSortMenu();
    renderRail();
    renderGrid();
    renderChips();
    renderPagination();
    renderClearAll();
    renderActiveFilterCount();
    renderRailCount();
    syncCategoryTileStates();
  }

  /* Category tiles are SSR'd inside the sort bar's __categories nav as
     anchor links (always all whitelist values, no SSR selected-state on
     /search). Tiles are navigation, not filter toggles — clicking one
     navigates away to /collections/all?filter.p.m.custom.maincategory=X.
     This sync exists for the edge case where /search is deep-linked with
     a maincategory filter already in the URL: it reflects that state on
     the tile via data-selected + aria-current so the user sees their
     "current" category highlighted before navigating elsewhere. */
  function syncCategoryTileStates() {
    const tiles = document.querySelectorAll('[data-category-tile-link]');
    const active = state.filtersByTitle['custom.maincategory'] || [];
    tiles.forEach(function (tile) {
      const isOn = active.indexOf(tile.dataset.categoryValue) !== -1;
      tile.dataset.selected = isOn ? 'true' : 'false';
      if (isOn) {
        tile.setAttribute('aria-current', 'page');
      } else {
        tile.removeAttribute('aria-current');
      }
    });
  }

  /* ===========================================================
     Render: title + counts
     =========================================================== */
  function renderTitle() {
    const el = document.querySelector(SEL.pageTitle);
    if (!el) return;
    el.replaceChildren();
    if (!state.query) {
      el.textContent = 'All products';
      return;
    }
    /* Two-element title: "Results" stays prominent; everything after
       (for "query") echoes in a smaller / lighter span so the line
       reads as label + value and stays short enough not to wrap. */
    el.appendChild(document.createTextNode('Results '));
    const echoSpan = document.createElement('span');
    echoSpan.className = 'c-plp-sort-bar__title-query';
    echoSpan.textContent = 'for "' + state.query + '"';
    el.appendChild(echoSpan);
  }

  function renderResultsCount() {
    const el = document.querySelector(SEL.resultsCount);
    if (!el) return;
    const total = state.response.total;
    el.textContent = total + ' result' + (total === 1 ? '' : 's');
  }

  function renderActiveFilterCount() {
    const el = document.querySelector(SEL.activeFilterCount);
    if (!el) return;
    const count = countActiveFilters();
    if (count > 0) {
      el.textContent = '(' + count + ')';
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  function renderRailCount() {
    const el = document.querySelector(SEL.railCount);
    if (!el) return;
    const count = countActiveFilters();
    if (count > 0) {
      el.textContent = '(' + count + ')';
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  }

  function countActiveFilters() {
    let count = 0;
    Object.keys(state.filtersByTitle).forEach(function (title) {
      count += state.filtersByTitle[title].length;
    });
    return count;
  }

  /* ===========================================================
     Render: sort menu
     =========================================================== */
  function renderSortMenu() {
    const menu = document.querySelector(SEL.sortMenu);
    const label = document.querySelector(SEL.sortLabel);
    if (!menu) return;

    menu.replaceChildren();
    VISIBLE_SORT_OPTIONS.forEach(function (opt) {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'c-plp-sort-bar__option';
      btn.dataset.sortOption = '';
      btn.dataset.sortValue = opt.value;
      const isActive = opt.value === state.sortValue;
      btn.dataset.active = isActive ? 'true' : 'false';
      /* Disclosure menu (not a listbox): mark the active sort with aria-current
         rather than role=option/aria-selected (matches plp-sort-bar.liquid). */
      if (isActive) btn.setAttribute('aria-current', 'true');
      btn.textContent = opt.label;
      li.appendChild(btn);
      menu.appendChild(li);
    });

    if (label) {
      const current = SORT_OPTIONS.find(function (o) { return o.value === state.sortValue; });
      label.textContent = current ? current.label : VISIBLE_SORT_OPTIONS[0].label;
    }
  }

  /* ===========================================================
     Render: rail
     =========================================================== */
  function renderRail() {
    const rail = document.querySelector(SEL.rail);
    if (!rail) return;

    // Rail entrance parity with the collection rail (see
    // component-plp-filter-group.css). First render lets the option/header
    // @starting-style reveal play; later renders (keystroke / filter / sort)
    // mount under .is-rerendering for two frames so transitions are suppressed
    // and the rail lands instantly instead of re-staggering.
    if (rail.dataset.railRevealInit === 'true') {
      rail.classList.add('is-rerendering');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          rail.classList.remove('is-rerendering');
        });
      });
    }
    rail.dataset.railRevealInit = 'true';

    rail.replaceChildren();

    /* Merchant-configurable allow-list + order + label overrides via the
       section setting `search_rail_facets`. Format per line:
         <title>            -> use auto-derived label
         <title>|<label>    -> use custom label

       Empty setting = fall back to RAIL_HIDDEN_FACETS exclusion logic
       with auto-derived labels for everything not hidden. */
    const railFacetsRaw = rail.dataset.railFacets || '';
    const directives = railFacetsRaw
      .split('\n')
      .map(function (line) { return line.trim(); })
      /* Skip blanks AND comments (lines starting with #). Lets the
         merchant keep disabled facets visible in the textarea as a
         reference list — uncomment to enable. */
      .filter(function (line) { return line && line.charAt(0) !== '#'; })
      .map(function (line) {
        const parts = line.split('|');
        return {
          title: parts[0].trim(),
          labelOverride: parts.length > 1 ? parts.slice(1).join('|').trim() : null
        };
      });

    /* Cache the title -> label override map on state so renderChips and
       any other surfaces that need a facet's display label can use it. */
    state.labelOverrides = {};
    directives.forEach(function (d) {
      if (d.labelOverride) state.labelOverrides[d.title] = d.labelOverride;
    });

    let facets;
    if (directives.length > 0) {
      /* Allow-list mode: render facets in the order listed, applying
         label overrides where given. Empty-bucket facets dropped silently. */
      facets = directives
        .map(function (d) {
          const found = state.response.facets.find(function (f) { return f.title === d.title; });
          if (!found) return null;
          if (d.labelOverride) {
            /* Clone shallowly so we don't mutate the response object. */
            return Object.assign({}, found, { label: d.labelOverride });
          }
          return found;
        })
        .filter(function (f) { return f && f.buckets && f.buckets.length > 0; });
    } else {
      facets = state.response.facets.filter(function (f) {
        return !RAIL_HIDDEN_FACETS.has(f.title) &&
               !RAIL_HIDDEN_FACETS.has(f.label) &&
               f.buckets && f.buckets.length > 0;
      });
    }

    /* TEMPORARILY HIDDEN (not selling / no stock management yet): Price and
       Availability. Remove this filter to restore them; renderPriceFacet and the
       in-stock toggle rendering are intentionally left intact. Applies in both
       allow-list and fallback modes. Mirror of the collection-side skip in
       plp-side-drawer.liquid. */
    facets = facets.filter(function (f) {
      return f.title !== 'Price' && f.title !== 'Availability';
    });

    facets.forEach(function (facet) {
      const node = renderFacetGroup(facet);
      if (node) rail.appendChild(node);
    });

    if (facets.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'c-plp-side-drawer__empty';
      empty.textContent = 'No filters available for this search.';
      rail.appendChild(empty);
    }

    /* Groups are rebuilt collapsed; plp-side-drawer.js owns the reveal state and
       restores it. Runs inside the is-rerendering window so nothing animates. */
    if (window.PlpFacetReveal) window.PlpFacetReveal.apply(rail);
  }

  function facetLabel(title) {
    /* Single source of truth for the displayed label of a facet by title.
       Checks merchant overrides first, then falls back to the auto-derived
       label from the response. */
    if (state.labelOverrides && state.labelOverrides[title]) {
      return state.labelOverrides[title];
    }
    const facet = state.response && state.response.facets.find(function (f) { return f.title === title; });
    return facet ? facet.label : title;
  }

  function renderFacetGroup(facet) {
    if (facet.type === 'slider') return renderPriceFacet(facet);
    if (facet.type === 'select' && isBooleanFacet(facet)) return renderToggleFacet(facet);
    if (facet.type === 'select') return renderListFacet(facet);
    /* Unknown type: skip silently rather than render broken markup. */
    return null;
  }

  function isBooleanFacet(facet) {
    /* Availability is the canonical case: two buckets ("In Stock" / "Out of
       Stock"), rendered as a single "In stock only" toggle. */
    return facet.title === 'Availability' || (facet.buckets.length === 2 &&
      facet.buckets.some(function (b) { return /in.stock/i.test(b.value); }));
  }

  function renderListFacet(facet) {
    const details = document.createElement('details');
    details.className = 'c-accordion c-filter-group';
    details.dataset.facet = facetKey(facet);
    details.dataset.optsExpanded = 'false';
    details.open = true;

    const summary = document.createElement('summary');
    summary.className = 'c-accordion__head';

    const headLabel = document.createElement('span');
    headLabel.className = 'c-accordion__head-label';
    headLabel.textContent = facet.label.toUpperCase();

    const activeCount = activeValuesFor(facet.title).length;
    if (activeCount > 0) {
      const count = document.createElement('span');
      count.className = 'c-filter-group__head-count';
      count.textContent = '(' + activeCount + ')';
      headLabel.appendChild(document.createTextNode(' '));
      headLabel.appendChild(count);
    }

    const toggle = document.createElement('span');
    toggle.className = 'c-expand-toggle';
    toggle.setAttribute('aria-hidden', 'true');

    summary.appendChild(headLabel);
    summary.appendChild(toggle);
    details.appendChild(summary);

    const body = document.createElement('div');
    body.className = 'c-accordion__body c-filter-group__body';

    const ul = document.createElement('ul');
    ul.className = 'c-filter-group__options';
    ul.setAttribute('role', 'list');

    const rail = document.querySelector(SEL.rail);
    const visibleCount = parseInt(rail.dataset.facetOptionsVisible || '6', 10);

    /* Drop dead-end values (count 0 and not currently active). Mirror of the
       collection-side skip in plp-side-drawer.liquid. */
    const filteredBuckets = facet.buckets.filter(function (bucket) {
      const isActive = activeValuesFor(facet.title).indexOf(bucket.value) !== -1;
      if (bucket.count === 0 && !isActive) return false;
      return true;
    });
    if (filteredBuckets.length === 0) return null;

    filteredBuckets.forEach(function (bucket, i) {
      const li = document.createElement('li');
      li.className = 'c-filter-group__option-item';
      li.dataset.overflow = i >= visibleCount ? 'true' : 'false';
      // Per-option stagger index for the entrance reveal (see
      // component-plp-filter-group.css); mirrors the collection rail's
      // rendered_index in plp-side-drawer.liquid.
      li.style.setProperty('--index', i + 1);

      const isActive = activeValuesFor(facet.title).indexOf(bucket.value) !== -1;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'c-filter-group__option';
      btn.dataset.searchaniseFilterTitle = facet.title;
      btn.dataset.searchaniseFilterValue = bucket.value;
      btn.dataset.selected = isActive ? 'true' : 'false';
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');

      const check = document.createElement('span');
      check.className = 'c-filter-group__option-check';
      check.setAttribute('aria-hidden', 'true');

      const labelSpan = document.createElement('span');
      labelSpan.className = 'c-filter-group__option-label';
      labelSpan.textContent = bucket.value;

      const countSpan = document.createElement('span');
      countSpan.className = 'c-filter-group__option-count';
      countSpan.textContent = '(' + bucket.count + ')';

      btn.appendChild(check);
      btn.appendChild(labelSpan);
      btn.appendChild(countSpan);
      li.appendChild(btn);
      ul.appendChild(li);
    });

    body.appendChild(ul);

    if (filteredBuckets.length > visibleCount) {
      const showAll = document.createElement('button');
      showAll.type = 'button';
      showAll.className = 'c-filter-group__show-all';
      showAll.dataset.facetShowAll = '';
      showAll.setAttribute('aria-expanded', 'false');

      const showAllText = document.createElement('span');
      showAllText.dataset.showAllText = '';
      showAllText.textContent = 'Show all ' + filteredBuckets.length;

      const showLessText = document.createElement('span');
      showLessText.dataset.showLessText = '';
      showLessText.hidden = true;
      showLessText.textContent = 'Show fewer';

      showAll.appendChild(showAllText);
      showAll.appendChild(showLessText);
      body.appendChild(showAll);
    }

    details.appendChild(body);
    return details;
  }

  function renderToggleFacet(facet) {
    /* Match the canonical common-toggle-switch markup exactly so the
       existing component-common-toggle-switch.css picks it up: button
       carries c-toggle-switch, body order is track (with slider) then
       label. Previous attempt used __thumb (wrong class) and reversed
       order (broke the layout). */
    const wrap = document.createElement('div');
    wrap.className = 'c-filter-group c-filter-group--toggle';
    wrap.dataset.facet = facetKey(facet);

    const inStockBucket = facet.buckets.find(function (b) { return /in.stock/i.test(b.value); });
    const filterValue = inStockBucket ? inStockBucket.value : 'In Stock';
    const selected = activeValuesFor(facet.title).indexOf(filterValue) !== -1;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'c-toggle-switch';
    btn.dataset.searchaniseFilterTitle = facet.title;
    btn.dataset.searchaniseFilterValue = filterValue;
    btn.dataset.selected = selected ? 'true' : 'false';
    btn.setAttribute('aria-pressed', selected ? 'true' : 'false');

    const track = document.createElement('span');
    track.className = 'c-toggle-switch__track';
    track.setAttribute('aria-hidden', 'true');
    const slider = document.createElement('span');
    slider.className = 'c-toggle-switch__slider';
    track.appendChild(slider);

    const labelSpan = document.createElement('span');
    labelSpan.className = 'c-toggle-switch__label';
    labelSpan.textContent = 'In stock only';

    btn.appendChild(track);
    btn.appendChild(labelSpan);
    wrap.appendChild(btn);
    return wrap;
  }

  function renderPriceFacet(facet) {
    /* Match the canonical price-filter markup in plp-side-drawer.liquid:
       name="filter.v.price.gte" / "lte" (Shopify URL convention so form
       posts work as a fallback), min/max constraints from the facet's
       range bucket, inputmode="numeric" for mobile keyboards, currency
       symbol in placeholder ("$ min" hardcoded since we're US-only). */
    const wrap = document.createElement('div');
    wrap.className = 'c-filter-group c-filter-group--price';
    wrap.dataset.facet = facetKey(facet);

    const row = document.createElement('div');
    row.className = 'c-filter-group__price-row';

    const label = document.createElement('span');
    label.className = 'c-filter-group__price-label';
    label.textContent = (facet.label || 'PRICE').toUpperCase();

    const range = facet.buckets[0] || {};
    const rangeMin = range.from || '';
    const rangeMax = range.to || '';
    const currentMin = (state.filtersByTitle['Price.min'] || [])[0] || '';
    const currentMax = (state.filtersByTitle['Price.max'] || [])[0] || '';

    const minInput = document.createElement('input');
    minInput.type = 'number';
    minInput.className = 'c-filter-group__price-input';
    minInput.name = 'filter.v.price.gte';
    minInput.placeholder = '$ min';
    minInput.value = currentMin;
    if (rangeMin !== '') minInput.min = rangeMin;
    if (rangeMax !== '') minInput.max = rangeMax;
    minInput.dataset.searchanisePrice = 'min';
    minInput.setAttribute('inputmode', 'numeric');

    const dash = document.createElement('span');
    dash.className = 'c-filter-group__price-dash';
    dash.setAttribute('aria-hidden', 'true');
    dash.textContent = '-';

    const maxInput = document.createElement('input');
    maxInput.type = 'number';
    maxInput.className = 'c-filter-group__price-input';
    maxInput.name = 'filter.v.price.lte';
    maxInput.placeholder = '$ max';
    maxInput.value = currentMax;
    if (rangeMin !== '') maxInput.min = rangeMin;
    if (rangeMax !== '') maxInput.max = rangeMax;
    maxInput.dataset.searchanisePrice = 'max';
    maxInput.setAttribute('inputmode', 'numeric');

    row.appendChild(label);
    row.appendChild(minInput);
    row.appendChild(dash);
    row.appendChild(maxInput);
    wrap.appendChild(row);
    return wrap;
  }

  function facetKey(facet) {
    /* Stable hook for CSS selectors / future JS lookups. Strip prefix
       and snake_case to dash-case: "custom.surface_type" -> "surface-type". */
    return (facet.title || '').replace(/^custom\./, '').replace(/_/g, '-').toLowerCase();
  }

  function activeValuesFor(title) {
    return state.filtersByTitle[title] || [];
  }

  /* ===========================================================
     Render: grid (product cards)
     =========================================================== */
  function renderGrid() {
    const list = document.querySelector(SEL.gridList);
    const empty = document.querySelector(SEL.gridEmpty);
    if (!list) return;

    list.replaceChildren();
    const all = state.response.products || [];
    const products = state.clientSort
      ? all.slice((state.page - 1) * PAGE_SIZE, state.page * PAGE_SIZE)
      : all;

    if (products.length === 0) {
      if (empty) {
        empty.textContent = state.query
          ? 'No products match "' + state.query + '".'
          : 'No products match the current filters.';
        empty.hidden = false;
      }
      return;
    }

    if (empty) empty.hidden = true;

    // Card reveal parity with the collection grid (see component-plp-grid.css +
    // plp-card-reveal.js). First render staggers cards in as they enter the
    // viewport; later renders (keystroke / filter / sort) mount cards already
    // revealed so they appear instantly — a freshly-inserted final-state
    // element never transitions, so no flash and no replayed stagger.
    const firstRender = list.dataset.cardRevealInit !== 'true';

    products.forEach(function (product) {
      const card = renderProductCard(product);
      if (!firstRender) card.setAttribute('data-revealed', 'true');
      list.appendChild(card);
    });

    if (firstRender) {
      list.dataset.cardRevealInit = 'true';
      if (window.PlpCardReveal) {
        window.PlpCardReveal.observe(list);
      } else {
        // No reveal module: show cards so the html[data-js] hide can't trap them.
        list.querySelectorAll('.c-product-card').forEach(function (card) {
          card.setAttribute('data-revealed', 'true');
        });
      }
    }
  }

  function renderProductCard(product) {
    const li = document.createElement('li');
    li.className = 'c-product-card';

    const imgWrap = document.createElement('a');
    imgWrap.className = 'c-product-card__image';
    imgWrap.href = product.url || '#';
    /* Same destination as the title link below. Hidden from a11y tree + tab
       order so each card exposes one link, not two (matches common-product-card.liquid). */
    imgWrap.setAttribute('aria-hidden', 'true');
    imgWrap.setAttribute('tabindex', '-1');

    if (product.imageUrl) {
      const img = document.createElement('img');
      img.className = 'c-product-card__photo';
      img.src = product.imageUrl;
      img.alt = product.title || '';
      img.loading = 'lazy';
      /* Match the SSR card's intrinsic square (CLS parity). The shared
         .c-product-card__photo CSS object-fit:covers, so it crops, not distorts. */
      img.width = 600;
      img.height = 600;
      imgWrap.appendChild(img);
    }

    if (product.vendor) {
      const brand = document.createElement('span');
      brand.className = 'c-product-card__brand-chip';
      brand.textContent = product.vendor.toUpperCase();
      imgWrap.appendChild(brand);
    }

    const body = document.createElement('div');
    body.className = 'c-product-card__body';

    const title = document.createElement('h3');
    title.className = 'c-product-card__title';
    const titleLink = document.createElement('a');
    titleLink.href = product.url || '#';
    titleLink.textContent = product.title || '';
    title.appendChild(titleLink);
    body.appendChild(title);

    /* BV inline rating, in flow between the title and the CTA (mirrors
       common-product-card.liquid). product.id is the Shopify product_id from the
       Searchanise feed. These cards render after bv.js's initial DOM scan, so they
       hydrate only once a BV re-scan is called after renderGrid (pending BV's API). */
    if (product.id) {
      const rating = document.createElement('div');
      rating.className = 'c-product-card__rating';
      rating.setAttribute('data-bv-show', 'inline_rating');
      rating.setAttribute('data-bv-product-id', product.id);
      rating.setAttribute('data-bv-redirect-url', product.url || '');
      rating.setAttribute('data-bv-seo', 'false');
      body.appendChild(rating);
    }

    if (product.price) {
      const price = document.createElement('span');
      price.className = 'c-product-card__price';
      price.textContent = product.price;
      body.appendChild(price);
    }

    /* FIND LOCAL button (matches common-find-local-button.liquid). Reads
       theme settings from the global mount element rendered by
       common-find-local-dialog.liquid in theme.liquid. If the mount is
       absent (URL template unset in theme settings), the button is omitted. */
    const findLocalMount = document.querySelector('[data-find-local-mount]');
    if (findLocalMount) {
      const urlTemplate = findLocalMount.dataset.findLocalUrlTemplate || '';
      const buttonLabel = findLocalMount.dataset.findLocalButtonLabel || 'WHERE TO BUY';
      const buttonBehavior = findLocalMount.dataset.findLocalButtonBehavior || 'modal';
      const ariaTemplate = findLocalMount.dataset.findLocalAriaTemplate || '';
      const productGroup = product.productGroup || product.product_group || '';

      let cta;
      if (buttonBehavior === 'new_tab') {
        const resolvedUrl = urlTemplate
          .replace('{product_group}', encodeURIComponent(productGroup))
          .replace('{variants}', '');
        cta = document.createElement('a');
        cta.className = 'c-find-local-button';
        cta.href = resolvedUrl;
        cta.target = '_blank';
        cta.rel = 'noopener';
      } else {
        cta = document.createElement('button');
        cta.type = 'button';
        cta.className = 'c-find-local-button';
        cta.dataset.findLocalTrigger = '';
        cta.dataset.productGroup = productGroup;
        if (product.url) cta.dataset.productUrl = product.url;
      }
      cta.textContent = buttonLabel;
      /* Per-product accessible name so screen-reader users can tell the
         otherwise-identical FIND LOCAL buttons apart (matches the aria-label in
         common-find-local-button.liquid). */
      if (ariaTemplate && product.title) {
        cta.setAttribute('aria-label', ariaTemplate.replace('__NAME__', product.title));
      }
      body.appendChild(cta);
    }

    li.appendChild(imgWrap);
    li.appendChild(body);
    return li;
  }

  /* ===========================================================
     Render: chips (active filter banner)
     =========================================================== */
  function renderChips() {
    const chips = document.querySelector(SEL.chips);
    if (!chips) return;
    chips.replaceChildren();

    Object.keys(state.filtersByTitle).forEach(function (title) {
      const values = state.filtersByTitle[title];
      const groupLabel = facetLabel(title);
      values.forEach(function (value) {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'c-plp-filter-banner__chip';
        btn.dataset.searchaniseFilterTitle = title;
        btn.dataset.searchaniseFilterValue = value;
        btn.dataset.selected = 'true';
        btn.setAttribute('aria-label', 'Remove filter: ' + groupLabel + ' ' + value);

        const labelSpan = document.createElement('span');
        labelSpan.className = 'c-plp-filter-banner__chip-label';
        labelSpan.textContent = value;

        const dismiss = document.createElement('span');
        dismiss.className = 'c-plp-filter-banner__chip-dismiss';
        dismiss.setAttribute('aria-hidden', 'true');
        dismiss.textContent = '×';

        btn.appendChild(labelSpan);
        btn.appendChild(dismiss);
        li.appendChild(btn);
        chips.appendChild(li);
      });
    });
  }

  function renderClearAll() {
    const clearAlls = document.querySelectorAll(SEL.clearAll);
    const hasActive = countActiveFilters() > 0;
    clearAlls.forEach(function (el) {
      el.hidden = !hasActive;
      el.href = '#';
    });
  }

  /* ===========================================================
     Render: pagination
     Matches the canonical common-pagination markup exactly so the
     existing component-common-pagination.css picks it up: nav wrapper
     with data-loading-dim, .c-pagination__controls flat div (no ul/li),
     <a> elements for active links, <span> for current/disabled, and
     the .c-pagination__range "Page X of Y" line beneath the controls.
     =========================================================== */
  function renderPagination() {
    const nav = document.querySelector(SEL.pagination);
    if (!nav) return;
    nav.replaceChildren();

    const total = state.response.total;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (totalPages <= 1) {
      nav.hidden = true;
      return;
    }
    nav.hidden = false;
    nav.setAttribute('aria-label', 'Pagination');

    const controls = document.createElement('div');
    controls.className = 'c-pagination__controls';

    /* Previous arrow */
    controls.appendChild(buildPageArrow(
      state.page > 1 ? state.page - 1 : null,
      '<',
      'Previous page'
    ));

    /* Numbered pages: compact window with ellipses */
    const pages = paginationWindow(state.page, totalPages);
    pages.forEach(function (p) {
      if (p === '…') {
        const ellipsis = document.createElement('span');
        ellipsis.className = 'c-pagination__controls-num';
        ellipsis.textContent = '…';
        controls.appendChild(ellipsis);
        return;
      }
      controls.appendChild(buildPageNum(p, p === state.page));
    });

    /* Next arrow */
    controls.appendChild(buildPageArrow(
      state.page < totalPages ? state.page + 1 : null,
      '>',
      'Next page'
    ));

    nav.appendChild(controls);

    /* "Page X of Y" range line */
    const range = document.createElement('div');
    range.className = 'c-pagination__range';
    range.textContent = 'Page ' + state.page + ' of ' + totalPages;
    nav.appendChild(range);
  }

  function buildPageArrow(targetPage, char, ariaLabel) {
    if (targetPage === null) {
      const span = document.createElement('span');
      span.className = 'c-pagination__controls-arrow';
      span.setAttribute('aria-disabled', 'true');
      span.setAttribute('aria-label', ariaLabel);
      span.textContent = char;
      return span;
    }
    const a = document.createElement('a');
    a.className = 'c-pagination__controls-arrow';
    a.href = pageUrl(targetPage);
    a.dataset.searchanisePage = String(targetPage);
    a.setAttribute('aria-label', ariaLabel);
    a.textContent = char;
    return a;
  }

  function buildPageNum(pageNum, isCurrent) {
    if (isCurrent) {
      const span = document.createElement('span');
      span.className = 'c-pagination__controls-num';
      span.setAttribute('aria-current', 'page');
      span.textContent = String(pageNum);
      return span;
    }
    const a = document.createElement('a');
    a.className = 'c-pagination__controls-num';
    a.href = pageUrl(pageNum);
    a.dataset.searchanisePage = String(pageNum);
    a.textContent = String(pageNum);
    return a;
  }

  function pageUrl(pageNum) {
    /* Build the URL that would result from navigating to this page so the
       <a href> shows the right destination on hover / middle-click. The
       controller's click handler intercepts via [data-searchanise-page]
       and skips the full navigation. */
    const saved = state.page;
    state.page = pageNum;
    const url = serializeStateToUrl().toString();
    state.page = saved;
    return url;
  }

  function paginationWindow(current, total) {
    /* Show first, last, neighbors of current, ellipses elsewhere. */
    if (total <= 7) {
      const arr = [];
      for (let i = 1; i <= total; i++) arr.push(i);
      return arr;
    }
    const set = new Set([1, 2, total - 1, total, current - 1, current, current + 1]);
    const arr = Array.from(set).filter(function (n) { return n >= 1 && n <= total; }).sort(function (a, b) { return a - b; });
    const out = [];
    for (let i = 0; i < arr.length; i++) {
      out.push(arr[i]);
      if (i < arr.length - 1 && arr[i + 1] - arr[i] > 1) out.push('…');
    }
    return out;
  }

  function renderError() {
    /* /search is a client-rendered shell: this message is the only content there
       is. Collections keep their SSR cards instead. Note this protects the first
       fetch, which is the case where ownership is declined and url-filter-controller
       keeps the page. A failure after ownership is earned leaves the previous
       results on screen. */
    if (!IS_SEARCH) return;

    const list = document.querySelector(SEL.gridList);
    const empty = document.querySelector(SEL.gridEmpty);
    if (list) list.replaceChildren();
    if (empty) {
      empty.textContent = 'Search is temporarily unavailable. Please try again in a moment.';
      empty.hidden = false;
    }
  }

  /* ===========================================================
     Events
     =========================================================== */
  function bindEvents() {
    document.addEventListener('click', handleClick);
    document.addEventListener('change', handleChange);
    document.addEventListener('keydown', handlePriceEnter);
    window.addEventListener('popstate', handlePopState);
  }

  function handleClick(event) {
    /* Filter option click — rail + chips (data-searchanise-filter-title) */
    const filterBtn = event.target.closest('[data-searchanise-filter-title]');
    if (filterBtn) {
      event.preventDefault();
      const title = filterBtn.dataset.searchaniseFilterTitle;
      const value = filterBtn.dataset.searchaniseFilterValue;
      toggleFilter(title, value);
      applyChange();
      return;
    }

    /* Category tile click (hero filter band) — uses the Shopify-style
       data-filter-tile contract so the same tile markup works on both
       /collections (via url-filter-controller.js) and /search (via this
       controller). Strip filter.p.m. prefix to get the facet title. */
    const tileBtn = event.target.closest('[data-filter-tile]');
    if (tileBtn) {
      event.preventDefault();
      const param = tileBtn.dataset.filterParam || '';
      const value = tileBtn.dataset.filterValue;
      const mode = tileBtn.dataset.filterMode || 'multi';
      const title = param.replace(/^filter\.(p\.m\.)?/, '');
      if (title && value) {
        toggleFilter(title, value, mode);
        applyChange();
      }
      return;
    }

    /* Sort option click */
    const sortBtn = event.target.closest('[data-sort-option]');
    if (sortBtn && document.querySelector(SEL.root)) {
      event.preventDefault();
      state.sortValue = sortBtn.dataset.sortValue;
      state.page = 1;
      /* Close the dropdown explicitly — re-render destroys the clicked
         element, but the wrapper's data-open + trigger's aria-expanded
         persist across the swap and would leave the dropdown visually
         open. */
      const dropdown = sortBtn.closest('[data-sort-dropdown]');
      if (dropdown) {
        dropdown.dataset.open = 'false';
        const trigger = dropdown.querySelector('[data-sort-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }
      applyChange();
      return;
    }

    /* Show-all / show-less inside a filter group */
    /* Show all / Show fewer is owned by plp-side-drawer.js, which is loaded on
       both collection and search pages and binds the same data-facet-show-all
       click on document. Handling it here too caused a double-toggle (both
       handlers flip data-opts-expanded on one click), which cancelled out on
       /search and made the reveal appear broken. */

    /* Clear all */
    const clearAllBtn = event.target.closest(SEL.clearAll);
    if (clearAllBtn && !clearAllBtn.hidden) {
      event.preventDefault();
      state.filtersByTitle = {};
      state.page = 1;
      applyChange();
      return;
    }

    /* Pagination click */
    const pageBtn = event.target.closest('[data-searchanise-page]');
    if (pageBtn) {
      event.preventDefault();
      const next = parseInt(pageBtn.dataset.searchanisePage, 10);
      if (!isNaN(next) && next > 0) {
        state.page = next;
        applyChange();
        scrollToGridTop();
      }
      return;
    }
  }

  function handleChange(event) {
    /* Price input change */
    const priceInput = event.target.closest('[data-searchanise-price]');
    if (priceInput) {
      applyPriceChange();
    }
  }

  function handlePriceEnter(event) {
    if (event.key !== 'Enter') return;
    const priceInput = event.target.closest('[data-searchanise-price]');
    if (priceInput) {
      event.preventDefault();
      applyPriceChange();
    }
  }

  function applyPriceChange() {
    const minInput = document.querySelector('[data-searchanise-price="min"]');
    const maxInput = document.querySelector('[data-searchanise-price="max"]');
    const min = minInput ? minInput.value : '';
    const max = maxInput ? maxInput.value : '';
    if (min) state.filtersByTitle['Price.min'] = [min];
    else delete state.filtersByTitle['Price.min'];
    if (max) state.filtersByTitle['Price.max'] = [max];
    else delete state.filtersByTitle['Price.max'];
    state.page = 1;
    applyChange();
  }

  function handlePopState() {
    parseUrlToState();
    fetchAndRender();
  }

  function toggleFilter(title, value, mode) {
    if (!state.filtersByTitle[title]) state.filtersByTitle[title] = [];
    const idx = state.filtersByTitle[title].indexOf(value);
    // Single-select facets replace the title's list on add and clear it on
    // re-click of the active value. Multi-select (default) appends or removes
    // the value while leaving any other values in the list intact.
    if (mode === 'single') {
      if (idx === -1) {
        state.filtersByTitle[title] = [value];
      } else {
        delete state.filtersByTitle[title];
      }
    } else {
      if (idx === -1) {
        state.filtersByTitle[title].push(value);
      } else {
        state.filtersByTitle[title].splice(idx, 1);
        if (state.filtersByTitle[title].length === 0) delete state.filtersByTitle[title];
      }
    }
    state.page = 1;
  }

  function applyChange() {
    pushUrlState();
    fetchAndRender();
  }

  function scrollToGridTop() {
    const root = document.querySelector(SEL.root);
    if (!root) return;
    if (root.dataset.filterOpen === 'true') return;
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
})();
